import React, { useState, useEffect, useCallback } from 'react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import Select from '../ui/Select';
import authService from '../../utils/authService';
import sessionService from '../../utils/sessionService.js';
import teamService from '../../utils/teamService';
import useFeatureFlags from '../../hooks/useFeatureFlags';

const CreateProjectModal = ({
  isOpen,
  onClose,
  onCreateProject,
  organizationId,
  organizationName,
}) => {
   const [formData, setFormData] = useState({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    projectManager: '',
    teamMembers: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [teamMembers, setTeamMembers] = useState([]);
  const [currentUserRole, setCurrentUserRole] = useState('member');

  // Feature flags - Bug #2 Fix: Check if user can create projects
  const { flags, loading: flagsLoading } = useFeatureFlags(organizationId);

  const loadTeamMembers = useCallback(async () => {
    try {
      const members = await teamService.getTeamMembers(organizationId);
      const memberOptions = members.map((member) => ({
        value: member.id,
        label: `${member.name} (${member.email})`,
        role: member.role,
      }));
      setTeamMembers(memberOptions);
    } catch (error) {
      console.error('Failed to load team members:', error);
    }
  }, [organizationId]);

  const loadUserRole = async () => {
    try {
      const userResponse = await authService.getCurrentUser();
      if (userResponse.data && userResponse.data.user) {
        setCurrentUserRole(
          userResponse.data.user.organizationRole ||
            userResponse.data.user.role ||
            sessionService.getUserRole() ||
            'member'
        );
      }
    } catch (error) {
      console.error('Failed to load user role:', error);
    }
  };

  // Load team members and user role when modal opens
  useEffect(() => {
    if (isOpen && organizationId) {
      loadTeamMembers();
      loadUserRole();
    }
  }, [isOpen, organizationId, loadTeamMembers]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name.trim()) {
      setError('Project name is required');
      return;
    }

    if (
      formData.startDate &&
      formData.endDate &&
      new Date(formData.startDate) > new Date(formData.endDate)
    ) {
      setError('End date must be after start date');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Normalize values coming from Select components
      const rawManagerId = typeof formData.projectManager === 'object'
        ? formData.projectManager?.value
        : formData.projectManager || null;

      // Strict UUID v4 pattern (accept lowercase/uppercase)
      const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;

      const managerId = rawManagerId && UUID_RE.test(String(rawManagerId)) ? String(rawManagerId) : null;

      const memberIds = Array.isArray(formData.teamMembers)
        ? formData.teamMembers
            .map(m => (typeof m === 'object' ? m.value : m))
            .filter(v => v && UUID_RE.test(String(v)))
            .map(v => String(v))
        : [];

      // Map to backend expected payload (snake_case keys)
      const projectData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        start_date: formData.startDate || null,
        due_date: formData.endDate || null,
        project_manager: managerId || null,
        team_members: memberIds,
      };



      await onCreateProject(projectData);

      // Reset form and close modal
      setFormData({
        name: '',
        description: '',
        startDate: '',
        endDate: '',
        projectManager: '',
        teamMembers: [],
      });
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to create project');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setFormData({
        name: '',
        description: '',
        startDate: '',
        endDate: '',
        projectManager: '',
        teamMembers: [],
      });
      setError('');
      onClose();
    }
  };

  if (!isOpen) return null;

  // Bug #2 Fix: Show restriction message if user cannot create projects
  if (!flagsLoading && !flags.adminProjectCreation) {
    return (
      <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1050] p-4'>
        <div className='bg-white rounded-xl shadow-2xl max-w-md w-full p-6'>
          <div className='text-center'>
            <div className='w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4'>
              <svg className='w-8 h-8 text-red-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z' />
              </svg>
            </div>
            <h3 className='text-xl font-semibold text-gray-900 mb-2'>Project Creation Restricted</h3>
            <p className='text-gray-600 mb-4'>
              Only organization owners and authorized administrators can create projects.
            </p>
            <p className='text-sm text-gray-500 mb-6'>
              Your current role: <strong className='text-gray-700'>{flags.userRole || 'member'}</strong>
            </p>
            <p className='text-sm text-gray-500 mb-6'>
              Please contact your organization owner to request project creation permissions or to have them create the project for you.
            </p>
            <Button
              variant='default'
              onClick={handleClose}
              className='w-full'
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='fixed inset-0 z-[1050] flex items-start justify-center p-4 pt-20'>
      {/* Backdrop with enhanced blur */}
      <div 
        className='absolute inset-0 bg-slate-900/40 backdrop-blur-md animate-in fade-in duration-300' 
        onClick={handleClose}
      />
      
      {/* Modal with glass-surface effect */}
      <div className='relative glass-surface rounded-3xl shadow-2xl max-w-lg w-full max-h-[90vh] flex flex-col overflow-hidden animate-in zoom-in-95 fade-in duration-300'>
        {/* Header */}
        <div className='flex items-center justify-between p-8 border-b border-white/20 dark:border-slate-700/50 flex-shrink-0'>
          <div>
            <h2 className='text-2xl font-black text-slate-800 dark:text-white tracking-tightest'>
              Create New Project
            </h2>
            <div className='flex items-center mt-1 space-x-2'>
              <span className='inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-primary-light text-primary border border-primary/20'>
                {organizationName}
              </span>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={isLoading}
            className='h-10 w-10 flex items-center justify-center rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-500 hover:text-slate-800 dark:hover:text-white transition-all hover:rotate-90 disabled:opacity-50'
          >
            <svg
              className='w-5 h-5'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2.5}
                d='M6 18L18 6M6 6l12 12'
              />
            </svg>
          </button>
        </div>

        {/* Form - Scrollable content */}
        <div className='overflow-y-auto custom-scrollbar'>
          <form onSubmit={handleSubmit} className='p-8 space-y-6'>
            {/* Project Name */}
            <div>
              <label
                htmlFor='projectName'
                className='block text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-2 ml-1'
              >
                Project Name *
              </label>
              <Input
                id='projectName'
                type='text'
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder='Enter project name'
                disabled={isLoading}
                className='w-full bg-white/50 dark:bg-slate-900/50 border-white/50 dark:border-slate-700/50 transition-all focus:ring-2 focus:ring-primary'
              />
            </div>

            {/* Project Description */}
            <div>
              <label
                htmlFor='projectDescription'
                className='block text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-2 ml-1'
              >
                Description
              </label>
              <Textarea
                id='projectDescription'
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder='Tell us about this project...'
                disabled={isLoading}
                rows={3}
                className='w-full bg-white/50 dark:bg-slate-900/50 border-white/50 dark:border-slate-700/50 transition-all focus:ring-2 focus:ring-primary'
              />
            </div>

            {/* Date Range */}
            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <div>
                <label
                  htmlFor='startDate'
                  className='block text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-2 ml-1'
                >
                  Start Date
                </label>
                <Input
                  id='startDate'
                  type='date'
                  value={formData.startDate}
                  onChange={(e) => handleInputChange('startDate', e.target.value)}
                  disabled={isLoading}
                  className='w-full bg-white/50 dark:bg-slate-900/50 border-white/50 dark:border-slate-700/50'
                />
              </div>
              <div>
                <label
                  htmlFor='endDate'
                  className='block text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-2 ml-1'
                >
                  End Date
                </label>
                <Input
                  id='endDate'
                  type='date'
                  value={formData.endDate}
                  onChange={(e) => handleInputChange('endDate', e.target.value)}
                  disabled={isLoading}
                  className='w-full bg-white/50 dark:bg-slate-900/50 border-white/50 dark:border-slate-700/50'
                  min={formData.startDate}
                />
              </div>
            </div>

            {/* Project Manager */}
            <div>
              <label
                htmlFor='projectManager'
                className='block text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-2 ml-1'
              >
                Project Manager
              </label>
              <Select
                id='projectManager'
                options={[
                  { value: '', label: 'Select project manager (optional)' },
                  ...teamMembers.filter((member) =>
                    ['admin', 'owner'].includes(member.role)
                  ),
                ]}
                value={formData.projectManager}
                onChange={(value) => handleInputChange('projectManager', value)}
                disabled={isLoading}
                className='w-full bg-white/50 dark:bg-slate-900/50'
                placeholder='Select project manager'
              />
            </div>

            {/* Team Members */}
            <div>
              <label
                htmlFor='teamMembers'
                className='block text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-2 ml-1'
              >
                Team Members
              </label>
              <Select
                id='teamMembers'
                options={teamMembers}
                value={formData.teamMembers}
                onChange={(value) => handleInputChange('teamMembers', value)}
                disabled={isLoading}
                multiple={true}
                dropup={true}
                className='w-full bg-white/50 dark:bg-slate-900/50'
                placeholder='Collaborators...'
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className='p-4 bg-red-500/10 border border-red-500/20 rounded-2xl animate-in shake-in duration-300'>
                <p className='text-sm font-medium text-red-600 dark:text-red-400 flex items-center'>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  {error}
                </p>
              </div>
            )}

            {/* Actions */}
            <div className='flex items-center justify-end gap-4 pt-4'>
              <Button
                type='button'
                variant='ghost'
                onClick={handleClose}
                disabled={isLoading}
                className='font-bold'
              >
                Cancel
              </Button>
              <Button
                type='submit'
                variant='default'
                disabled={isLoading || !formData.name.trim()}
                iconName={isLoading ? 'Loader2' : 'Plus'}
                iconPosition='left'
                className={`rounded-xl px-8 py-3 shadow-lg shadow-primary/20 font-bold transition-all ${
                  isLoading ? 'animate-pulse' : 'hover:scale-105 active:scale-95'
                }`}
              >
                {isLoading ? 'Creating...' : 'Create Project'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateProjectModal;
